// Ship loadout screen (spec-loadout §1): shown before the first run and
// re-openable from the pause menu. Two sections — pick 1 of 3 slot-0 weapons,
// then spend 5 trait points (0–4 each) across SWIFT / BULWARK / STRIKER —
// a live procedural preview of the ship those choices produce, and a LAUNCH
// confirm that persists the choice and hands it to the game.
// Mirrors upgrade-overlay.ts idioms: root overlay div, injected styles,
// pixel-bordered panel via theme.ts helpers, 44px touch targets, safe-area
// padding, mobile-first single column (two columns at 768px+).

import type { Loadout, LoadoutTraits, LoadoutWeaponId } from '../sim/loadout.js';
import {
  LOADOUT_WEAPON_IDS,
  TRAIT_POINTS_MAX_PER_TRAIT,
  TRAIT_POINTS_TOTAL,
  getTraitMods,
  normalizeLoadout,
  rollWeaponSlots
} from '../sim/loadout.js';
import type { PortraitStyle, WeaponDef } from '../types.js';
import { DEFAULT_SETTINGS, loadLoadout, saveLoadout } from './storage.js';
import { colors, ensureUiStyles, FONT_UI, icon, pixelShadow } from './theme.js';
import { renderShipPreview } from '../render/ship-preview.js';
import { captainFromSeed } from '../captain/seed.js';
import { compositeCaptain } from '../captain/compositor.js';
import { describeEffects } from '../captain/effects.js';
import { rollCaptainQuirks, rollShipQuirks, type Quirk } from '../sim/quirks.js';
import { rollShipName } from '../sim/ship-names.js';
import weaponsData from '../content/weapons.json';

export interface LoadoutScreenCallbacks {
  /** Called with the confirmed loadout (already persisted to storage). */
  onConfirm(loadout: Loadout): void;
  /** Seed of the ship the next run will use; `reroll: true` rolls a new one. */
  getShipSeed?(reroll?: boolean): number;
  /** Seed of the captain the next run hires; `reroll: true` hires someone else. */
  getCaptainSeed?(reroll?: boolean): number;
  /** Captain portrait style from settings; absent = the settings default. */
  getPortraitStyle?(): PortraitStyle;
}

/** Read the persisted loadout without constructing the screen (boot gate). */
export function readLoadout(): Loadout {
  return loadLoadout();
}

const WEAPON_DEFS = (weaponsData as { version: string; entries: WeaponDef[] }).entries;

function weaponDisplayName(id: LoadoutWeaponId): string {
  return WEAPON_DEFS.find((d) => d.id === id)?.display_name ?? id;
}

/** Render a quirks list: one small colored line per quirk (muted common,
 * mint rare, gold epic). */
function renderQuirkList(el: HTMLElement, quirks: readonly Quirk[]): void {
  el.textContent = '';
  for (const q of quirks) {
    const line = document.createElement('div');
    line.className = `la-quirk-${q.rarity}`;
    line.textContent = q.rarity === 'common' ? q.text : `${q.text} ★`;
    el.appendChild(line);
  }
}

const WEAPON_PRESENTATION: Readonly<Record<LoadoutWeaponId, { icon: string; blurb: string }>> = {
  'weapon.spear-jelly-lance': {
    icon: 'bolt',
    blurb: 'Balanced auto lance — steady bolts at the nearest foe.'
  },
  'weapon.shard-scattergun': {
    icon: 'expand-solid',
    blurb: 'Close-range 5-shard fan. Get in their face.'
  },
  'weapon.seeker-swarm': {
    icon: 'star-crescent-solid',
    blurb: 'Homing missiles that hunt on their own.'
  }
};

const TRAIT_PRESENTATION: readonly {
  id: keyof LoadoutTraits;
  name: string;
  icon: string;
  effect: string;
}[] = [
  { id: 'swift', name: 'SWIFT', icon: 'bolt-solid', effect: '+4% move speed, +4% dash recharge, −4% max HP' },
  { id: 'bulwark', name: 'BULWARK', icon: 'heart-solid', effect: '+12 max HP, +5% shield regen, −3% move speed' },
  { id: 'striker', name: 'STRIKER', icon: 'fire-solid', effect: '+5% damage, +3% fire rate, −6% max HP · +1 hardpoint at 3+' }
];

const STYLE_ID = 'la-loadout-styles';

function ensureLoadoutStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
/* Lumen Abyss loadout screen — see src/ui/loadout-screen.ts.
   Mobile-first: single-column sections, stepping up to weapons-left /
   traits-right at 768px and larger type at 1600px. */
.la-loadout-root {
  position: absolute; inset: 0; display: none; box-sizing: border-box;
  align-items: center; justify-content: center;
  background: rgba(5, 10, 20, 0.45);
  z-index: 50;
  font-family: ${FONT_UI}; color: ${colors.text}; font-size: 11px;
  text-shadow: 0 2px 0 rgba(0, 0, 0, 0.7);
  padding: env(safe-area-inset-top) env(safe-area-inset-right)
           env(safe-area-inset-bottom) env(safe-area-inset-left);
}
.la-loadout-panel {
  display: flex; flex-direction: column; align-items: stretch; gap: 16px;
  box-sizing: border-box;
  background: ${colors.panelBg}; box-shadow: ${pixelShadow(colors.accent, 4)};
  padding: 16px; width: min(420px, 100%);
  max-width: calc(100vw - 24px);
  max-width: calc(100vw - 24px - env(safe-area-inset-left) - env(safe-area-inset-right));
  max-height: calc(100vh - 24px);
  max-height: calc(100vh - 24px - env(safe-area-inset-top) - env(safe-area-inset-bottom));
  max-height: calc(100dvh - 24px - env(safe-area-inset-top) - env(safe-area-inset-bottom));
  overflow-y: auto; overscroll-behavior: contain; pointer-events: auto;
}
/* The title band (from the design wireframe): full-width blue-bordered box
 * with the game name in gold. */
.la-loadout-title {
  margin: 0; font-size: 16px; font-weight: 700; color: ${colors.accent};
  display: flex; align-items: center; justify-content: center; gap: 10px;
  background: ${colors.barTrack}; box-shadow: ${pixelShadow(colors.panelBorder, 3)};
  padding: 10px 16px; letter-spacing: 4px;
}
.la-loadout-hint {
  margin: 0; font-size: 9px; line-height: 1.6; color: ${colors.muted};
  text-align: center;
}
.la-loadout-sections { display: grid; grid-template-columns: 1fr; gap: 16px; }
.la-loadout-section { display: flex; flex-direction: column; gap: 8px; }
.la-loadout-section-label {
  font-size: 9px; letter-spacing: 1px; color: ${colors.muted};
  text-transform: uppercase; text-align: center;
}
.la-loadout-weapons { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
.la-loadout-weapon {
  display: grid; align-items: center; text-align: left;
  grid-template-columns: auto 1fr;
  grid-template-areas: 'icon name' 'icon blurb';
  column-gap: 12px; row-gap: 4px;
  width: 100%; min-height: 56px; padding: 12px 16px; box-sizing: border-box;
  background: ${colors.barTrack}; color: ${colors.text};
  border: 0; border-radius: 0; box-shadow: ${pixelShadow()};
  font-family: inherit; font-size: inherit;
  cursor: pointer; pointer-events: auto;
  touch-action: manipulation; user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.la-loadout-weapon:hover, .la-loadout-weapon:focus-visible {
  box-shadow: ${pixelShadow(colors.accent)}; color: ${colors.accent}; outline: none;
}
.la-loadout-weapon.mounted {
  opacity: 0.55; cursor: default; pointer-events: none;
}
.la-loadout-weapon.selected {
  box-shadow: ${pixelShadow(colors.accent, 5)}; color: ${colors.accent};
}
.la-loadout-weapon .la-loadout-weapon-icon {
  grid-area: icon; font-size: 22px; color: ${colors.accent};
}
.la-loadout-weapon-name { grid-area: name; font-weight: 700; }
.la-loadout-weapon-blurb {
  grid-area: blurb; color: ${colors.muted}; font-size: 9px; line-height: 1.5;
}
.la-loadout-weapon:hover .la-loadout-weapon-blurb,
.la-loadout-weapon:focus-visible .la-loadout-weapon-blurb,
.la-loadout-weapon.selected .la-loadout-weapon-blurb { color: ${colors.text}; }
.la-loadout-traits { display: flex; flex-direction: column; gap: 8px; }
.la-loadout-trait {
  display: grid; align-items: center;
  grid-template-columns: 1fr auto;
  grid-template-areas: 'name stepper' 'effect effect';
  column-gap: 10px; row-gap: 4px;
  padding: 12px 16px; box-sizing: border-box;
  background: ${colors.barTrack}; box-shadow: ${pixelShadow()};
}
.la-loadout-trait-name {
  grid-area: name; display: flex; align-items: center; gap: 8px;
  font-weight: 700;
}
.la-loadout-trait-name .la-loadout-trait-icon { color: ${colors.accent}; }
.la-loadout-trait-effect {
  grid-area: effect; color: ${colors.muted}; font-size: 9px; line-height: 1.5;
}
.la-loadout-stepper {
  grid-area: stepper; display: flex; align-items: center; gap: 8px;
}
.la-loadout-step {
  display: flex; align-items: center; justify-content: center;
  width: 44px; height: 44px; box-sizing: border-box;
  background: ${colors.panelBg}; color: ${colors.text};
  border: 0; border-radius: 0; box-shadow: ${pixelShadow()};
  font-family: inherit; font-size: 14px;
  cursor: pointer; pointer-events: auto;
  touch-action: manipulation; user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.la-loadout-step:hover, .la-loadout-step:focus-visible {
  box-shadow: ${pixelShadow(colors.accent)}; color: ${colors.accent}; outline: none;
}
.la-loadout-step:disabled {
  opacity: 0.35; cursor: default;
  box-shadow: ${pixelShadow()}; color: ${colors.text};
}
.la-loadout-pips { display: flex; align-items: center; gap: 3px; }
.la-loadout-pip {
  width: 8px; height: 12px; box-sizing: border-box;
  box-shadow: inset 0 0 0 2px ${colors.panelBorder};
}
.la-loadout-pip.filled { background: ${colors.accent}; }
.la-loadout-points {
  font-size: 10px; color: ${colors.accent}; text-align: center;
}
.la-loadout-crew {
  display: flex; justify-content: center; align-items: stretch;
  gap: 16px; margin: 2px 0 8px; flex-wrap: wrap;
}
.la-loadout-card {
  position: relative; display: flex; flex-direction: column;
  align-items: stretch; gap: 6px; flex: 1 1 280px; min-width: 0;
  background: ${colors.panelBg}; box-shadow: ${pixelShadow(colors.panelBorder)};
  padding: 8px;
}
.la-loadout-card-label {
  font-size: 8px; letter-spacing: 2px; color: ${colors.muted}; text-align: center;
}
.la-loadout-card-body {
  display: flex; align-items: center; gap: 10px; flex: 1;
}
.la-loadout-card-desc {
  display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1;
  text-align: left; align-items: flex-start;
}
.la-loadout-captain-canvas {
  width: 96px; height: 96px; image-rendering: pixelated; flex: none;
  background: ${colors.barTrack}; box-shadow: ${pixelShadow(colors.accent)};
}
.la-loadout-captain-name, .la-loadout-ship-name {
  color: ${colors.accent}; font-weight: 700; text-align: left;
  font-size: 10px; word-break: break-word;
}
.la-loadout-captain-meta {
  font-size: 8px; color: ${colors.muted}; letter-spacing: 1px; text-align: left;
}
.la-loadout-captain-fx {
  display: flex; flex-direction: column; align-items: flex-start; gap: 1px;
  font-size: 8px; letter-spacing: 1px;
}
.la-fx-plus { color: #3dffc4; }
.la-fx-minus { color: #ff4d6b; }
.la-loadout-quirks {
  display: flex; flex-direction: column; align-items: flex-start; gap: 1px;
  font-size: 8px; letter-spacing: 1px; text-align: left;
  margin-top: 2px;
}
.la-loadout-hardpoints {
  font-size: 8px; letter-spacing: 1px; text-align: left;
  color: ${colors.accent};
}
.la-quirk-common { color: ${colors.muted}; }
.la-quirk-rare { color: #3dffc4; }
.la-quirk-epic { color: ${colors.accent}; text-shadow: 0 0 6px rgba(255, 211, 107, 0.6); }
.la-loadout-ship-canvas {
  width: 96px; height: 96px; image-rendering: pixelated; flex: none;
  background: ${colors.barTrack}; box-shadow: ${pixelShadow(colors.accent)};
}
.la-loadout-canvaswrap {
  position: relative; display: inline-block; line-height: 0;
}
.la-loadout-reroll {
  position: absolute; right: 4px; bottom: 4px;
  width: 28px; height: 28px; border: 0; border-radius: 0;
  background: rgba(7, 21, 38, 0.92); color: ${colors.accent};
  box-shadow: ${pixelShadow(colors.panelBorder, 2)};
  font-family: inherit; font-size: 13px; line-height: 1;
  cursor: pointer; pointer-events: auto; touch-action: manipulation;
}
.la-loadout-reroll:hover, .la-loadout-reroll:focus-visible {
  box-shadow: ${pixelShadow(colors.accent)}; outline: none;
}
.la-loadout-footer {
  display: flex; flex-direction: column; align-items: center; gap: 8px;
}
/* Phones: the footer (points left + LAUNCH) is sticky — pinned to the
   bottom of the scrollport with its own background and a divider, so
   LAUNCH is always reachable without scrolling to the end. */
@media (max-width: 767px) {
  .la-loadout-footer {
    position: sticky; bottom: 0; z-index: 2;
    /* Nearly opaque: scrolling content must not bleed through the pin. */
    background: rgba(10, 27, 46, 0.98);
    margin: 0 -16px -16px;
    padding: 8px 16px 16px;
    box-shadow: 0 -3px 0 0 ${colors.panelBorder};
  }
  /* The sticky pin stops 16px short of the panel's bottom edge (its bottom
     padding), leaving a strip where scrolling content shows through — this
     apron extends the footer background over it. */
  .la-loadout-footer::after {
    content: ''; position: absolute; left: 0; right: 0; top: 100%;
    height: 16px; background: inherit;
  }
}
/* SHIP SUMMARY: aggregated build totals (traits + hardpoints), recomputed
   live as points move. A compact row of stat chips under the trait strip. */
.la-loadout-summary {
  display: flex; flex-direction: column; gap: 6px;
  background: ${colors.barTrack}; box-shadow: ${pixelShadow()};
  padding: 8px 12px;
}
.la-loadout-summary-label {
  font-size: 9px; letter-spacing: 2px; color: ${colors.muted};
  text-transform: uppercase; text-align: center;
}
.la-loadout-summary-row {
  display: flex; flex-wrap: wrap; justify-content: center; gap: 4px 14px;
}
.la-loadout-summary-stat {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 9px; letter-spacing: 1px; color: ${colors.text};
}
.la-loadout-summary-stat.dim { color: ${colors.muted}; }
.la-loadout-confirm {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  width: 100%; min-height: 48px; padding: 12px 24px; box-sizing: border-box;
  background: ${colors.barTrack}; color: ${colors.accent}; font-weight: 700;
  border: 0; border-radius: 0; box-shadow: ${pixelShadow(colors.accent)};
  font-family: inherit; font-size: inherit;
  cursor: pointer; pointer-events: auto;
  touch-action: manipulation; user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.la-loadout-confirm:disabled {
  opacity: 0.35; cursor: not-allowed;
  box-shadow: ${pixelShadow(colors.panelBorder)};
}
.la-loadout-confirm:hover, .la-loadout-confirm:focus-visible {
  box-shadow: ${pixelShadow(colors.accent, 5)}; outline: none;
}
@media (min-width: 768px) {
  .la-loadout-root { font-size: 12px; }
  .la-loadout-panel {
    width: min(860px, 100%); gap: 16px; padding: 24px 32px;
  }
  .la-loadout-title { font-size: 20px; gap: 12px; }
  .la-loadout-hint { font-size: 10px; }
  /* Design wireframe: full-width bands — crew cards, then a 4-up weapon
   * strip (3 choices + mounted turret), then a 3-up trait strip. */
  .la-loadout-sections { grid-template-columns: 1fr; gap: 20px; }
  .la-loadout-weapons { grid-template-columns: repeat(4, 1fr); }
  .la-loadout-weapons .la-loadout-weapon { padding: 8px 12px; column-gap: 8px; }
  .la-loadout-traits { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
  .la-loadout-section-label { font-size: 10px; }
  .la-loadout-weapon-blurb, .la-loadout-trait-effect { font-size: 10px; }
  .la-loadout-confirm { width: auto; align-self: flex-end; padding: 12px 24px; }
  .la-loadout-footer { flex-direction: row; justify-content: space-between; align-items: center; }
}
@media (min-width: 1600px) {
  .la-loadout-root { font-size: 16px; }
  .la-loadout-panel { width: min(960px, 100%); }
  .la-loadout-title { font-size: 28px; }
  .la-loadout-hint { font-size: 13px; }
  .la-loadout-section-label { font-size: 13px; }
  .la-loadout-weapon-blurb, .la-loadout-trait-effect { font-size: 13px; }
  .la-loadout-points { font-size: 13px; }
}
/* Short landscape phones: tighter chrome; the panel scrolls if needed. */
@media (orientation: landscape) and (max-height: 500px) and (max-width: 1599px) {
  .la-loadout-panel { gap: 8px; padding: 12px 14px; }
  .la-loadout-title { font-size: 14px; }
  .la-loadout-sections { gap: 8px; }
  .la-loadout-weapon { padding: 8px 10px; }
  .la-loadout-trait { padding: 6px 8px; }
}
`;
  document.head.appendChild(style);
}

interface TraitRowRefs {
  minus: HTMLButtonElement;
  plus: HTMLButtonElement;
  pips: HTMLElement[];
}

export class LoadoutScreen {
  private readonly root: HTMLElement;
  private readonly panel: HTMLElement;
  private readonly callbacks: LoadoutScreenCallbacks;
  private loadout: Loadout = normalizeLoadout(null);
  private open = false;
  private weaponCards = new Map<LoadoutWeaponId, HTMLButtonElement>();
  private traitRows = new Map<keyof LoadoutTraits, TraitRowRefs>();
  private pointsLeftEl: HTMLElement | null = null;
  private summaryEl: HTMLElement | null = null;
  private previewCanvas: HTMLCanvasElement | null = null;
  private captainCanvas: HTMLCanvasElement | null = null;
  private captainNameEl: HTMLElement | null = null;
  private captainMetaEl: HTMLElement | null = null;
  private captainFxEl: HTMLElement | null = null;
  private captainQuirksEl: HTMLElement | null = null;
  private shipQuirksEl: HTMLElement | null = null;
  private shipHardpointsEl: HTMLElement | null = null;
  private shipNameEl: HTMLElement | null = null;
  private weaponSectionLabelEl: HTMLElement | null = null;
  private confirmBtn: HTMLButtonElement | null = null;
  private captainCardToken = 0;

  constructor(container: HTMLElement, callbacks: LoadoutScreenCallbacks) {
    ensureUiStyles();
    ensureLoadoutStyles();
    this.callbacks = callbacks;

    this.root = document.createElement('div');
    this.root.className = 'la-loadout-root';
    this.panel = document.createElement('div');
    this.panel.className = 'la-loadout-panel';
    this.root.appendChild(this.panel);
    container.appendChild(this.root);
  }

  /** Read the persisted loadout without opening the screen (boot gate). */
  static readLoadout(): Loadout {
    return loadLoadout();
  }

  isOpen(): boolean {
    return this.open;
  }

  /** The current in-screen selection (normalized copy). */
  getLoadout(): Loadout {
    return normalizeLoadout(this.loadout);
  }

  /** Open the screen with the persisted loadout as the starting selection. */
  show(): void {
    this.loadout = loadLoadout();
    if (!this.open) {
      this.open = true;
      window.addEventListener('keydown', this.onKeyDown);
    }
    this.render();
    this.root.style.display = 'flex';
  }

  hide(): void {
    if (!this.open) return;
    this.open = false;
    this.root.style.display = 'none';
    window.removeEventListener('keydown', this.onKeyDown);
  }

  dispose(): void {
    this.hide();
    this.root.remove();
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (!this.open) return;
    if (e.key === '1' || e.key === '2' || e.key === '3') {
      e.preventDefault();
      this.selectWeapon(LOADOUT_WEAPON_IDS[Number(e.key) - 1]);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      this.confirm();
    }
  };

  private pointsLeft(): number {
    return (
      TRAIT_POINTS_TOTAL -
      this.loadout.swift - this.loadout.bulwark - this.loadout.striker
    );
  }

  private selectWeapon(id: LoadoutWeaponId): void {
    this.loadout.weapon = id;
    for (const [weaponId, card] of this.weaponCards) {
      const selected = weaponId === id;
      card.classList.toggle('selected', selected);
      card.setAttribute('aria-pressed', selected ? 'true' : 'false');
    }
    this.refreshPreview();
  }

  private adjustTrait(id: keyof LoadoutTraits, delta: 1 | -1): void {
    const current = this.loadout[id];
    if (delta > 0 && (current >= TRAIT_POINTS_MAX_PER_TRAIT || this.pointsLeft() <= 0)) return;
    if (delta < 0 && current <= 0) return;
    this.loadout[id] = current + delta;
    this.updateTraitUI();
    this.refreshPreview();
  }

  /** Redraw the procedural ship preview for the current selection + seed. */
  private refreshPreview(): void {
    if (!this.previewCanvas) return;
    const seed = this.callbacks.getShipSeed ? this.callbacks.getShipSeed() : 1;
    renderShipPreview(
      this.previewCanvas,
      seed,
      { swift: this.loadout.swift, bulwark: this.loadout.bulwark, striker: this.loadout.striker },
      this.loadout.weapon
    );
    // Ship name: same seeded roll as the hull, so a reroll re-christens it.
    if (this.shipNameEl) {
      const name = rollShipName(seed);
      this.shipNameEl.textContent = name;
      this.shipNameEl.title = name;
    }
    // Hardpoint count comes from the same roll applyLoadout uses, so the
    // number previewed here (live with trait moves + hull rerolls) is the
    // number the run starts with.
    if (this.shipHardpointsEl) {
      this.shipHardpointsEl.textContent = `HARDPOINTS: ${rollWeaponSlots(seed, this.loadout.striker)}`;
      this.updateSummary();
    }
    if (this.weaponSectionLabelEl) {
      const slots = rollWeaponSlots(seed, this.loadout.striker);
      this.weaponSectionLabelEl.textContent = `Weapon · ${slots} auto slot${slots === 1 ? '' : 's'} + turret`;
    }
    if (this.shipQuirksEl) {
      renderQuirkList(this.shipQuirksEl, rollShipQuirks(seed));
    }
  }

  /** Redraw the captain card (portrait composites async from the atlas; the
   * placeholder bust serves until it lands). */
  private refreshCaptainCard(): void {
    if (!this.captainCanvas || !this.captainNameEl || !this.captainMetaEl) return;
    const seed = this.callbacks.getCaptainSeed ? this.callbacks.getCaptainSeed() : 1;
    const captain = captainFromSeed(seed);
    this.captainNameEl.textContent = captain.name;
    const temp = captain.temperament.toUpperCase();
    const pron = captain.pronouns === 'they' ? 'they/them' : captain.pronouns === 'she' ? 'she/her' : 'he/him';
    this.captainMetaEl.textContent = `${pron} · ${temp}`;
    if (this.captainFxEl) {
      const fx = describeEffects(captain);
      this.captainFxEl.textContent = '';
      const plus = document.createElement('span');
      plus.className = 'la-fx-plus';
      plus.textContent = fx.plus;
      const minus = document.createElement('span');
      minus.className = 'la-fx-minus';
      minus.textContent = fx.minus;
      this.captainFxEl.appendChild(plus);
      this.captainFxEl.appendChild(minus);
    }
    if (this.captainQuirksEl) {
      renderQuirkList(this.captainQuirksEl, rollCaptainQuirks(seed));
    }
    const token = ++this.captainCardToken;
    const style = this.callbacks.getPortraitStyle?.() ?? DEFAULT_SETTINGS.video.portrait_style;
    void compositeCaptain(captain, style).then((bust) => {
      if (token !== this.captainCardToken || !this.captainCanvas) return;
      const ctx = this.captainCanvas.getContext('2d');
      if (!ctx) return;
      ctx.imageSmoothingEnabled = false;
      ctx.clearRect(0, 0, 128, 128);
      ctx.drawImage(bust, 0, 0, 128, 128);
    });
  }

  private updateTraitUI(): void {
    const left = this.pointsLeft();
    for (const [id, refs] of this.traitRows) {
      const value = this.loadout[id];
      refs.pips.forEach((pip, i) => pip.classList.toggle('filled', i < value));
      refs.minus.disabled = value <= 0;
      refs.plus.disabled = value >= TRAIT_POINTS_MAX_PER_TRAIT || left <= 0;
    }
    if (this.pointsLeftEl) {
      this.pointsLeftEl.textContent = `POINTS LEFT: ${left}`;
    }
    // LAUNCH unlocks only once every point is assigned.
    if (this.confirmBtn) {
      this.confirmBtn.disabled = left > 0;
      this.confirmBtn.title = left > 0 ? `Assign all ${left} remaining point(s) to launch` : 'Launch';
    }
    this.updateSummary();
  }

  private confirm(): void {
    // Gate: LAUNCH stays locked until every trait point is assigned.
    if (this.pointsLeft() > 0) return;
    const loadout = this.getLoadout();
    saveLoadout(loadout);
    this.hide();
    this.callbacks.onConfirm(loadout);
  }

  private render(): void {
    this.panel.textContent = '';
    this.weaponCards.clear();
    this.traitRows.clear();
    this.pointsLeftEl = null;

    const title = document.createElement('h2');
    title.className = 'la-loadout-title';
    title.appendChild(icon('flag-solid'));
    title.appendChild(document.createTextNode('LUMEN ABYSS'));
    this.panel.appendChild(title);

    const hint = document.createElement('p');
    hint.className = 'la-loadout-hint';
    hint.textContent =
      'Traits reshape the hull: SWIFT flies a dart, BULWARK a barge, STRIKER a blade.';
    this.panel.appendChild(hint);

    // Crew row: the captain being hired (portrait, name, register) next to
    // the live ship preview. Both re-rollable independently.
    const crew = document.createElement('div');
    crew.className = 'la-loadout-crew';

    // Captain card.
    const capCard = document.createElement('div');
    capCard.className = 'la-loadout-card';
    const capLabel = document.createElement('div');
    capLabel.className = 'la-loadout-card-label';
    capLabel.textContent = 'YOUR CAPTAIN';
    capCard.appendChild(capLabel);
    const capWrap = document.createElement('div');
    capWrap.className = 'la-loadout-canvaswrap';
    this.captainCanvas = document.createElement('canvas');
    this.captainCanvas.width = 128;
    this.captainCanvas.height = 128;
    this.captainCanvas.className = 'la-loadout-captain-canvas';
    capWrap.appendChild(this.captainCanvas);
    if (this.callbacks.getCaptainSeed) {
      const capReroll = document.createElement('button');
      capReroll.className = 'la-loadout-reroll';
      capReroll.title = 'Hire a different captain';
      capReroll.setAttribute('aria-label', 'Hire a different captain');
      capReroll.textContent = '↻';
      capReroll.addEventListener('click', () => {
        this.callbacks.getCaptainSeed!(true);
        this.refreshCaptainCard();
      });
      capWrap.appendChild(capReroll);
    }
    capCard.appendChild(capWrap);
    // Horizontal card body: portrait left, description column right.
    const capBody = document.createElement('div');
    capBody.className = 'la-loadout-card-body';
    capBody.appendChild(capWrap);
    const capDesc = document.createElement('div');
    capDesc.className = 'la-loadout-card-desc';
    this.captainNameEl = document.createElement('div');
    this.captainNameEl.className = 'la-loadout-captain-name';
    capDesc.appendChild(this.captainNameEl);
    this.captainMetaEl = document.createElement('div');
    this.captainMetaEl.className = 'la-loadout-captain-meta';
    capDesc.appendChild(this.captainMetaEl);
    // The captain's perk/drawback pair, always shown: every captain
    // amplifies one thing and taxes another (captain/effects.ts).
    this.captainFxEl = document.createElement('div');
    this.captainFxEl.className = 'la-loadout-captain-fx';
    capDesc.appendChild(this.captainFxEl);
    // Randomized minor traits (sim/quirks.ts): mostly flavor, occasionally a
    // mechanical nudge — the re-roll fishes for a cool set too.
    this.captainQuirksEl = document.createElement('div');
    this.captainQuirksEl.className = 'la-loadout-quirks';
    capDesc.appendChild(this.captainQuirksEl);
    capBody.appendChild(capDesc);
    capCard.appendChild(capBody);
    crew.appendChild(capCard);

    // Ship preview: the exact procedural hull (seed + trait bias) and weapon
    // mount the next run gets, with a re-roll for a new hull seed.
    const shipCard = document.createElement('div');
    shipCard.className = 'la-loadout-card';
    const shipLabel = document.createElement('div');
    shipLabel.className = 'la-loadout-card-label';
    shipLabel.textContent = 'YOUR SHIP';
    shipCard.appendChild(shipLabel);
    const shipWrap = document.createElement('div');
    shipWrap.className = 'la-loadout-canvaswrap';
    this.previewCanvas = document.createElement('canvas');
    this.previewCanvas.width = 128;
    this.previewCanvas.height = 128;
    this.previewCanvas.className = 'la-loadout-ship-canvas';
    shipWrap.appendChild(this.previewCanvas);
    if (this.callbacks.getShipSeed) {
      const reroll = document.createElement('button');
      reroll.className = 'la-loadout-reroll';
      reroll.title = 'Roll a new hull';
      reroll.setAttribute('aria-label', 'Roll a new hull');
      reroll.textContent = '↻';
      reroll.addEventListener('click', () => {
        this.callbacks.getShipSeed!(true);
        this.refreshPreview();
      });
      shipWrap.appendChild(reroll);
    }
    shipCard.appendChild(shipWrap);
    // Horizontal card body: hull preview left, description column right.
    const shipBody = document.createElement('div');
    shipBody.className = 'la-loadout-card-body';
    shipBody.appendChild(shipWrap);
    const shipDesc = document.createElement('div');
    shipDesc.className = 'la-loadout-card-desc';
    // Ship name FIRST (mirrors the captain card): the seeded two-word name
    // of the previewed hull, rerolled together with the hull seed.
    this.shipNameEl = document.createElement('div');
    this.shipNameEl.className = 'la-loadout-ship-name';
    shipDesc.appendChild(this.shipNameEl);
    // Hull hardpoint count for the previewed seed (auto weapon slots; the
    // manual turret is always slotted for free).
    this.shipHardpointsEl = document.createElement('div');
    this.shipHardpointsEl.className = 'la-loadout-hardpoints';
    shipDesc.appendChild(this.shipHardpointsEl);
    this.shipQuirksEl = document.createElement('div');
    this.shipQuirksEl.className = 'la-loadout-quirks';
    shipDesc.appendChild(this.shipQuirksEl);
    shipBody.appendChild(shipDesc);
    shipCard.appendChild(shipBody);
    crew.appendChild(shipCard);
    this.panel.appendChild(crew);
    this.refreshCaptainCard();

    const sections = document.createElement('div');
    sections.className = 'la-loadout-sections';
    sections.appendChild(this.renderWeaponSection());
    sections.appendChild(this.renderTraitSection());
    this.panel.appendChild(sections);

    // Footer organism: points readout directly above the launch button,
    // centered as one unit (was: points floating at the trait column's end).
    const footer = document.createElement('div');
    footer.className = 'la-loadout-footer';
    this.pointsLeftEl = document.createElement('div');
    this.pointsLeftEl.className = 'la-loadout-points';
    footer.appendChild(this.pointsLeftEl);
    const confirm = document.createElement('button');
    confirm.className = 'la-loadout-confirm';
    confirm.appendChild(icon('play'));
    confirm.appendChild(document.createTextNode('LAUNCH'));
    confirm.addEventListener('click', () => this.confirm());
    this.confirmBtn = confirm;
    footer.appendChild(confirm);
    this.panel.appendChild(footer);

    // Initial selection/pip states.
    this.selectWeapon(this.loadout.weapon);
    this.updateTraitUI();
  }

  private renderWeaponSection(): HTMLElement {
    const section = document.createElement('div');
    section.className = 'la-loadout-section';

    const label = document.createElement('div');
    label.className = 'la-loadout-section-label';
    label.textContent = 'Weapon · auto slots + turret';
    this.weaponSectionLabelEl = label;
    section.appendChild(label);

    const grid = document.createElement('div');
    grid.className = 'la-loadout-weapons';
    for (const id of LOADOUT_WEAPON_IDS) {
      const presentation = WEAPON_PRESENTATION[id];
      const card = document.createElement('button');
      card.className = 'la-loadout-weapon';
      card.setAttribute('aria-pressed', 'false');

      const iconWrap = document.createElement('span');
      iconWrap.className = 'la-loadout-weapon-icon';
      iconWrap.appendChild(icon(presentation.icon));
      card.appendChild(iconWrap);

      const name = document.createElement('span');
      name.className = 'la-loadout-weapon-name';
      name.textContent = weaponDisplayName(id);
      card.appendChild(name);

      const blurb = document.createElement('span');
      blurb.className = 'la-loadout-weapon-blurb';
      blurb.textContent = presentation.blurb;
      card.appendChild(blurb);

      card.addEventListener('click', () => this.selectWeapon(id));
      this.weaponCards.set(id, card);
      grid.appendChild(card);
    }
    // The fourth row is the mounted turret — always equipped, not a choice:
    // it balances the column against the trait rows and explains slot 1.
    const turret = document.createElement('div');
    turret.className = 'la-loadout-weapon mounted';
    turret.setAttribute('aria-disabled', 'true');
    const turretIcon = document.createElement('span');
    turretIcon.className = 'la-loadout-weapon-icon';
    turretIcon.appendChild(icon('robot-solid'));
    turret.appendChild(turretIcon);
    const turretName = document.createElement('span');
    turretName.className = 'la-loadout-weapon-name';
    turretName.textContent = 'MANUAL LASER CANNON';
    turret.appendChild(turretName);
    const turretBlurb = document.createElement('span');
    turretBlurb.className = 'la-loadout-weapon-blurb';
    turretBlurb.textContent = 'Hold-ground turret · fires where you tap · always mounted';
    turret.appendChild(turretBlurb);
    grid.appendChild(turret);
    section.appendChild(grid);
    return section;
  }

  private renderTraitSection(): HTMLElement {
    const section = document.createElement('div');
    section.className = 'la-loadout-section';

    const label = document.createElement('div');
    label.className = 'la-loadout-section-label';
    label.textContent = `Traits · ${TRAIT_POINTS_TOTAL} points, ${TRAIT_POINTS_MAX_PER_TRAIT} max each`;
    section.appendChild(label);

    const rows = document.createElement('div');
    rows.className = 'la-loadout-traits';
    for (const trait of TRAIT_PRESENTATION) {
      rows.appendChild(this.makeTraitRow(trait));
    }
    section.appendChild(rows);

    // SHIP SUMMARY: the aggregated build totals under the trait strip.
    this.summaryEl = document.createElement('div');
    this.summaryEl.className = 'la-loadout-summary';
    section.appendChild(this.summaryEl);
    return section;
  }

  /** Recompute the SHIP SUMMARY chips from the current loadout (traits +
   * weapon + rolled hardpoints). Called on every point/weapon/reroll change. */
  private updateSummary(): void {
    if (!this.summaryEl) return;
    const mods = getTraitMods({ loadoutTraits: this.loadout } as unknown as Parameters<typeof getTraitMods>[0]);
    const baseHp = 100; // player.max_hp (config default; traits price against it)
    const hp = Math.max(1, Math.round(baseHp + mods.maxHpDelta));
    const seed = this.callbacks.getShipSeed ? this.callbacks.getShipSeed() : 1;
    const hardpoints = rollWeaponSlots(seed, this.loadout.striker);
    const pct = (v: number): string => `${v >= 0 ? '+' : ''}${Math.round(v * 100)}%`;
    const weapon = weaponDisplayName(this.loadout.weapon);

    this.summaryEl.textContent = '';
    const label = document.createElement('div');
    label.className = 'la-loadout-summary-label';
    label.textContent = 'Ship summary';
    this.summaryEl.appendChild(label);
    const row = document.createElement('div');
    row.className = 'la-loadout-summary-row';
    const stat = (iconName: string, text: string, dim = false): void => {
      const el = document.createElement('span');
      el.className = `la-loadout-summary-stat${dim ? ' dim' : ''}`;
      el.appendChild(icon(iconName, 10));
      el.appendChild(document.createTextNode(text));
      row.appendChild(el);
    };
    stat('heart-solid', `HP ${hp}`);
    stat('bolt', `SPD ${pct(mods.moveSpeedMult)}`);
    stat('fire-solid', `DMG ${pct(mods.damageMult)}`);
    stat('refresh', `RATE ${pct(mods.fireRateMult)}`);
    stat('plane', `DASH ${pct(mods.dashHasteBonus)}`);
    stat('octagon-check-solid', `REGEN ${pct(mods.shieldRegenMultBonus)}`);
    stat('plus', `SLOTS ${hardpoints}`);
    stat('play', weapon, true);
    this.summaryEl.appendChild(row);
  }

  private makeTraitRow(trait: {
    id: keyof LoadoutTraits;
    name: string;
    icon: string;
    effect: string;
  }): HTMLElement {
    const row = document.createElement('div');
    row.className = 'la-loadout-trait';

    const name = document.createElement('span');
    name.className = 'la-loadout-trait-name';
    const iconWrap = document.createElement('span');
    iconWrap.className = 'la-loadout-trait-icon';
    iconWrap.appendChild(icon(trait.icon));
    name.appendChild(iconWrap);
    name.appendChild(document.createTextNode(trait.name));
    row.appendChild(name);

    const stepper = document.createElement('span');
    stepper.className = 'la-loadout-stepper';

    const minus = document.createElement('button');
    minus.className = 'la-loadout-step';
    minus.setAttribute('aria-label', `${trait.name} minus one point`);
    minus.appendChild(icon('minus'));
    minus.addEventListener('click', () => this.adjustTrait(trait.id, -1));
    stepper.appendChild(minus);

    const pipsWrap = document.createElement('span');
    pipsWrap.className = 'la-loadout-pips';
    const pips: HTMLElement[] = [];
    for (let i = 0; i < TRAIT_POINTS_MAX_PER_TRAIT; i++) {
      const pip = document.createElement('span');
      pip.className = 'la-loadout-pip';
      pipsWrap.appendChild(pip);
      pips.push(pip);
    }
    stepper.appendChild(pipsWrap);

    const plus = document.createElement('button');
    plus.className = 'la-loadout-step';
    plus.setAttribute('aria-label', `${trait.name} plus one point`);
    plus.appendChild(icon('plus'));
    plus.addEventListener('click', () => this.adjustTrait(trait.id, 1));
    stepper.appendChild(plus);

    row.appendChild(stepper);

    const effect = document.createElement('span');
    effect.className = 'la-loadout-trait-effect';
    effect.textContent = `per point: ${trait.effect}`;
    row.appendChild(effect);

    this.traitRows.set(trait.id, { minus, plus, pips });
    return row;
  }
}
